import React from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path } from 'react-native-svg';
import { StyleSheet, useUnistyles, withUnistyles } from 'react-native-unistyles';
import Chip from './Chip';
import DropdownMenu from './DropdownMenu';
import Unicon from './Unicon';
import { Tooltip } from './Tooltip';

const UniImage = withUnistyles(Image);

const HERO_VARIANTS = ['radiatingFan'];

const hashString = (value) => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
};

const resolveHeroVariant = (heroVariant, seed) => {
    if (typeof heroVariant === 'number') return HERO_VARIANTS[Math.abs(heroVariant) % HERO_VARIANTS.length];
    const aliases = {
        // topographic: 'topographic',
        // ribbons: 'topographic',
        fan: 'radiatingFan',
        radiatingFan: 'radiatingFan',
    };
    if (heroVariant && heroVariant !== 'random' && aliases[heroVariant]) return aliases[heroVariant];
    return HERO_VARIANTS[hashString(seed) % HERO_VARIANTS.length];
};

const AbstractHero = ({ moduleColor, variant }) => {
    const { theme: { colors } } = useUnistyles();
    const accent = colors[moduleColor] || colors.primary;
    const container = colors[`${moduleColor}Container`] || colors.primaryContainer;

    const patterns = {
        // topographic: (
        //     <>
        //         <Path d="M-28 94 C34 34 87 132 150 70 S267 14 324 72 S420 115 458 54" fill="none" stroke={accent} strokeWidth="2" opacity={0.36} />
        //         <Path d="M-38 112 C29 51 91 148 158 88 S274 31 333 88 S421 130 462 73" fill="none" stroke={accent} strokeWidth="2" opacity={0.27} />
        //         <Path d="M-18 72 C42 14 91 110 145 50 S258 -7 316 51 S406 96 448 34" fill="none" stroke={accent} strokeWidth="2" opacity={0.45} />
        //         <Path d="M30 150 C59 106 112 126 135 102 C167 68 151 38 198 24 C248 9 263 66 300 62 C337 58 351 17 402 2" fill="none" stroke={accent} strokeWidth="1.5" opacity={0.3} />
        //         <Path d="M78 150 C94 119 137 137 159 109 C184 77 174 51 210 40 C249 28 270 83 306 78 C347 73 365 38 424 23" fill="none" stroke={accent} strokeWidth="1.5" opacity={0.22} />
        //     </>
        // ),
        radiatingFan: (
            <>
                <Path d="M58 154 L-25 97 L-25 16 Z" fill={accent} opacity={0.12} />
                <Path d="M58 154 L-25 16 L61 -18 Z" fill={accent} opacity={0.28} />
                <Path d="M58 154 L61 -18 L150 -18 Z" fill={accent} opacity={0.15} />
                <Path d="M58 154 L150 -18 L253 -18 Z" fill={accent} opacity={0.35} />
                <Path d="M58 154 L253 -18 L363 -18 Z" fill={accent} opacity={0.18} />
                <Path d="M58 154 L363 -18 L482 25 L452 71 Z" fill={accent} opacity={0.3} />
                <Path d="M58 154 L452 71 L452 154 Z" fill={accent} opacity={0.11} />
            </>
        ),
    };

    return (
        <LinearGradient colors={[container, colors.surfaceVariant]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.defaultHero}>
            <Svg width="100%" height="100%" viewBox="0 0 420 140" preserveAspectRatio="xMidYMid slice">
                {patterns[variant]}
            </Svg>
        </LinearGradient>
    );
};

const renderBadge = (badge, index) => {
    if (React.isValidElement(badge)) return React.cloneElement(badge, { key: badge.key || index });
    if (typeof badge === 'string' || typeof badge === 'number') return <Chip key={index}>{badge}</Chip>;
    if (!badge) return null;
    return (
        <Chip
            key={badge.key || badge.label || index}
            colorVariant={badge.colorVariant || 'textSecondary'}
            icon={badge.icon}
            iconLibrary={badge.library}
            testID={badge.testID}
        >
            {badge.label}
        </Chip>
    );
};

const ResourceCard = ({
    variant = 'hero',
    heroImage,
    heroVariant = 'random',
    heroSeed,
    avatar,
    title,
    subtitle,
    date,
    stats = [],
    cta = null,
    menuOptions,
    badges = [],
    eyebrow,
    active = false,
    moduleColor = 'primary',
    onPress,
    testID = 'ResourceCard',
    selfSizing = false,
    flat = false,
    style,
}) => {
    const compact = variant === 'compact';
    const ctaConfig = typeof cta === 'string' ? { label: cta } : cta;
    const heroImageUri = typeof heroImage === 'string' ? heroImage : heroImage?.uri;
    const [heroImageFailed, setHeroImageFailed] = React.useState(false);
    React.useEffect(() => setHeroImageFailed(false), [heroImageUri]);
    const handleCta = (event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        (ctaConfig?.onPress || onPress)?.();
    };
    const handleSubtitlePress = (event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        onPress?.();
    };
    const resolvedHeroVariant = resolveHeroVariant(
        heroVariant,
        String(heroSeed ?? `${testID}|${typeof title === 'string' ? title : 'ResourceCard'}`),
    );
    const hero = React.isValidElement(heroImage)
        ? heroImage
        : heroImage && !heroImageFailed
            ? <UniImage source={typeof heroImage === 'string' ? { uri: heroImage } : heroImage} contentFit="cover" style={styles.heroImage} onError={() => setHeroImageFailed(true)} />
            : <AbstractHero moduleColor={moduleColor} variant={resolvedHeroVariant} />;
    const menu = !!menuOptions?.length && (
        <View
            onPointerDown={(event) => event?.stopPropagation?.()}
            onTouchEnd={(event) => event?.stopPropagation?.()}
            style={styles.menu(compact)}
        >
            <DropdownMenu
                shouldStopPropagation
                testID={`${testID}-menu`}
                options={menuOptions}
                triggerComponent={<Unicon name="ellipsis-horizontal" size={22} colorVariant="text" />}
            />
        </View>
    );

    return (
        <Pressable
            accessibilityRole={onPress && Platform.OS !== 'web' ? 'button' : undefined}
            accessibilityLabel={typeof title === 'string' ? title : undefined}
            disabled={!onPress}
            testID={testID}
            onPress={onPress}
            style={[styles.card(active, moduleColor, selfSizing, flat), style]}
        >
            {!compact && (
                <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" testID={`${testID}-hero`} style={styles.hero}>
                    {hero}
                    {!!date && (
                        <View style={styles.heroDateChip} pointerEvents="none">
                            <Text style={styles.heroDateText} numberOfLines={1}>{date}</Text>
                        </View>
                    )}
                </View>
            )}
            {!compact && menu}
            <View style={styles.content(compact, !!avatar, selfSizing)}>
                {!compact && avatar && (
                    <View pointerEvents="none" testID={`${testID}-avatar`} style={styles.avatar(false)}>
                        {avatar}
                    </View>
                )}
                <View style={styles.main(selfSizing)}>
                    <View style={styles.topRow}>
                        {compact && avatar && (
                            <View pointerEvents="none" testID={`${testID}-avatar`} style={styles.avatar(true)}>
                                {avatar}
                            </View>
                        )}
                        <View style={styles.heading}>
                            {!!eyebrow && <Text style={styles.eyebrow(moduleColor)} numberOfLines={1}>{eyebrow}</Text>}
                            <Text style={styles.title(compact)} numberOfLines={compact ? 1 : 2}>{title}</Text>
                        </View>
                        {compact && menu}
                    </View>
                    {!!subtitle && (typeof subtitle === 'string'
                        ? (
                            <Tooltip title={subtitle} onPress={onPress ? handleSubtitlePress : undefined} style={styles.subtitleTooltip}>
                                <Text style={styles.subtitle} numberOfLines={2}>{subtitle}</Text>
                            </Tooltip>
                        )
                        : subtitle)}
                    {compact && !!date && <Text style={styles.date}>{date}</Text>}
                    {!!badges.length && <View style={styles.badges}>{badges.map(renderBadge)}</View>}
                    {!!stats.length && (
                        <View style={styles.stats(compact)}>
                            {stats.filter(Boolean).map((stat, index) => {
                                const icon = typeof stat.icon === 'string' ? stat.icon : stat.icon?.name;
                                const library = stat.library || stat.icon?.library;
                                const value = stat.value ?? stat.count;
                                const text = value !== undefined && value !== null ? `${value}${stat.label ? ` ${stat.label}` : ''}` : stat.label;
                                return (
                                    <View key={stat.key || stat.name || stat.label || index} testID={stat.testID} style={styles.stat}>
                                        {!!icon && <Unicon name={icon} library={library} size={13} colorVariant="textTertiary" />}
                                        {!!stat.name && <Text style={styles.statName}>{stat.name}</Text>}
                                        {!!text && <Text style={styles.statText}>{text}</Text>}
                                    </View>
                                );
                            })}
                        </View>
                    )}
                </View>
                {!!ctaConfig?.label && (
                    <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={typeof title === 'string' ? `${ctaConfig.label} ${title}` : ctaConfig.label}
                        testID={`${testID}-cta`}
                        onPointerDown={(event) => { event?.stopPropagation?.(); event?.preventDefault?.(); }}
                        onPress={handleCta}
                        style={styles.cta(moduleColor)}
                    >
                        <Text style={styles.ctaText(moduleColor)}>{ctaConfig.label}</Text>
                        <Unicon name="arrow-forward-outline" size={14} colorVariant={moduleColor} />
                    </Pressable>
                )}
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography, roundness }) => ({
    card: (active, moduleColor, selfSizing, flat) => ({
        flex: selfSizing ? undefined : 1,
        minWidth: 0,
        flexDirection: 'column',
        backgroundColor: active ? (colors[`${moduleColor}Container`] || colors.primaryContainer) : colors.surface,
        borderRadius: roundness.lg,
        boxShadow: flat ? shadows.none : (active ? shadows.cardHover : shadows.card),
        overflow: 'hidden',
        _web: {
            cursor: 'pointer',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            _hover: { transform: flat ? undefined : [{ translateY: -2 }], boxShadow: flat ? shadows.none : shadows.cardHover },
        },
    }),
    hero: {
        height: 86,
        overflow: 'hidden',
        backgroundColor: colors.surfaceVariant,
    },
    heroDateChip: {
        position: 'absolute',
        top: spacing.sm,
        left: spacing.sm,
        paddingHorizontal: spacing.sm,
        paddingVertical: 2,
        borderRadius: roundness.xl,
        backgroundColor: colors.backgroundTransparent,
        boxShadow: shadows.card,
        zIndex: 2,
    },
    heroDateText: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
    },
    defaultHero: {
        width: '100%',
        height: '100%',
    },
    heroImage: {
        width: '100%',
        height: '100%',
    },
    content: (compact, hasAvatar, selfSizing) => ({
        flex: selfSizing ? undefined : 1,
        minWidth: 0,
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: compact ? spacing.md : 12,
        paddingTop: !compact && hasAvatar ? spacing.xl + spacing.xs : (compact ? spacing.md : 12),
        gap: compact ? spacing.sm : spacing.md,
    }),
    avatar: (compact) => ({
        position: compact ? 'relative' : 'absolute',
        top: compact ? undefined : -20,
        left: compact ? undefined : 12,
        padding: compact ? 0 : 3,
        borderRadius: roundness.xxl,
        backgroundColor: colors.surface,
        boxShadow: compact ? shadows.none : shadows.card,
        zIndex: 2,
    }),
    main: (selfSizing) => ({
        flex: selfSizing ? undefined : 1,
        minWidth: 0,
        gap: spacing.sm,
    }),
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    heading: {
        flex: 1,
        minWidth: 0,
        gap: spacing.xs,
    },
    eyebrow: (moduleColor) => ({
        color: colors[moduleColor] || colors.primary,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
    }),
    title: (compact) => ({
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: compact ? typography.body : typography.h2,
    }),
    subtitleTooltip: {
        alignSelf: 'stretch',
    },
    subtitle: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        lineHeight: 18,
    },
    date: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: typography.micro,
    },
    menu: (compact) => ({
        position: compact ? 'relative' : 'absolute',
        top: compact ? undefined : spacing.sm,
        right: compact ? undefined : spacing.sm,
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.backgroundTransparent,
        boxShadow: shadows.card,
        zIndex: 3,
        _web: {
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    }),
    badges: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: spacing.xs,
    },
    stats: (compact) => ({
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: compact ? spacing.sm : spacing.md,
        rowGap: spacing.xs,
    }),
    stat: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
    },
    statName: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: typography.caption,
    },
    statText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
    },
    cta: (moduleColor) => ({
        alignSelf: 'flex-end',
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        borderRadius: roundness.sm,
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        backgroundColor: colors[`${moduleColor}Container`] || colors.primaryContainer,
        _web: { cursor: 'pointer' },
    }),
    ctaText: (moduleColor) => ({
        color: colors[moduleColor] || colors.primary,
        fontFamily: fonts.bold,
        fontSize: typography.caption,
    }),
}));

export default ResourceCard;
